import type { PlayerError, PlayerErrorCode } from "./interface.js";

export class PlayerFailure extends Error {
  constructor(
    public readonly code: PlayerErrorCode,
    message: string,
    public readonly recoverable: boolean,
    public readonly sourceUrl?: string,
  ) {
    super(message);
    this.name = "PlayerFailure";
  }
}

export function toPlayerError(error: unknown, sourceUrl?: string): PlayerError {
  if (error instanceof PlayerFailure) {
    const safeUrl = sanitizeUrl(error.sourceUrl ?? sourceUrl);
    return {
      code: error.code,
      message: error.message,
      recoverable: error.recoverable,
      ...(safeUrl ? { sourceUrl: safeUrl } : {}),
    };
  }
  const safeUrl = sanitizeUrl(sourceUrl);
  return {
    code: "media",
    message: "The stream could not be played.",
    recoverable: true,
    ...(safeUrl ? { sourceUrl: safeUrl } : {}),
  };
}

/** Keep provider credentials out of reactive state, logs, and UI error payloads. */
export function sanitizeUrl(value: string | undefined): string | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    url.username = "";
    url.password = "";
    for (const key of [...url.searchParams.keys()]) {
      if (/^(?:user(?:name)?|pass(?:word)?|pwd|token|auth|api[_-]?key)$/i.test(key)) url.searchParams.delete(key);
    }
    const nested = url.searchParams.get("url");
    if (nested) {
      const safeNested = sanitizeUrl(nested);
      if (safeNested) url.searchParams.set("url", safeNested);
    }
    return url.toString();
  } catch {
    return "[redacted stream URL]";
  }
}
